import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { ThemeController } from '../../styles/tokens/ThemeController';
import type { ThemeEffect } from '@workspace/design-tokens';
import { DEFAULT_THEME_EFFECT, isThemeEffect } from '@workspace/design-tokens';

export type ThemeOption =
  | 'auto'
  | 'light'
  | 'dark'
  | 'midnight'
  | 'purple-dark'
  | 'bubblegum'
  | 'ocean'
  | 'forest'
  | 'aurora'
  | 'ember'
  | 'citrus'
  | 'sandstone'
  | 'slate'
  | 'glacier'
  | 'cyberpunk'
  | 'rainbow'
  | 'retro'
  | 'noir';

export type LayoutOption = 'side-menu' | 'top-menu';
export type ToastPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type MapBasemapTheme = Exclude<ThemeOption, 'auto'>;

export interface NavigationPreferences {
  favorites: string[];
  recents: string[];
  collapsed: boolean;
  favoritesOrder: 'alpha' | 'manual';
}

const DEFAULT_NAVIGATION_PREFERENCES: NavigationPreferences = {
  favorites: [],
  recents: [],
  collapsed: false,
  favoritesOrder: 'alpha'
};

export const DEFAULT_THEME: ThemeOption = 'auto';
export const DEFAULT_LAYOUT: LayoutOption = 'side-menu';
export const DEFAULT_TOAST_POSITION: ToastPosition = 'bottom-right';
export const DEFAULT_BASEMAP_THEME: MapBasemapTheme = 'light';

const LAYOUT_STORAGE_KEY = 'dashboard.layout';
const NAV_FAVORITES_KEY = 'ui.navigation.favorites';
const NAV_RECENTS_KEY = 'ui.navigation.recents';
const NAV_COLLAPSED_KEY = 'ui.navigation.collapsed';
const NAV_FAVORITES_ORDER_KEY = 'ui.navigation.favorites_order';
const NAV_FAVORITES_ORDER_KEY_LEGACY = 'ui.navigation.favoritesOrder';
const THEME_EFFECT_KEY = 'ui.theme.effect';
const TOAST_POSITION_KEY = 'ui.theme.toast_position';
const BASEMAP_THEME_KEY = 'maps.basemap_theme';
const BASEMAP_THEME_OVERRIDE_KEY = 'maps.basemap_theme_override';
const PREFERENCES_URL = `/api/v1/preferences/effective?keys=ui.theme,${THEME_EFFECT_KEY},ui.layout,${TOAST_POSITION_KEY},${BASEMAP_THEME_KEY},${BASEMAP_THEME_OVERRIDE_KEY},${NAV_FAVORITES_KEY},${NAV_RECENTS_KEY},${NAV_COLLAPSED_KEY},${NAV_FAVORITES_ORDER_KEY},${NAV_FAVORITES_ORDER_KEY_LEGACY}`;
const DASHBOARD_THEME_LOG_PREFIX = '[DashboardTheme]';

function logDashboardTheme(message: string, details?: Record<string, unknown>) {
  if (details) {
    console.log(`${DASHBOARD_THEME_LOG_PREFIX} ${message}`, details);
    return;
  }
  console.log(`${DASHBOARD_THEME_LOG_PREFIX} ${message}`);
}

type SavePreferencesPayload = {
  theme?: ThemeOption;
  themeEffect?: ThemeEffect;
  layout?: LayoutOption;
  toastPosition?: ToastPosition;
  basemapTheme?: MapBasemapTheme;
  basemapThemeOverrideEnabled?: boolean;
  navigation?: Partial<NavigationPreferences>;
};

type SavePreferencesOptions = {
  skipRevalidate?: boolean;
  resolvedNavigation?: NavigationPreferences;
};

type EffectivePreferencesResponse = {
  keys?: Record<string, { value?: unknown }>;
};

interface PreferencesContextValue {
  theme: ThemeOption;
  savedTheme: ThemeOption;
  themeEffect: ThemeEffect;
  savedThemeEffect: ThemeEffect;
  isThemePreview: boolean;
  previewTheme: (next: ThemeOption) => void;
  cancelThemePreview: () => void;
  setThemeEffect: (next: ThemeEffect) => void;
  layout: LayoutOption;
  savedLayout: LayoutOption;
  setLayout: (next: LayoutOption) => void;
  toastPosition: ToastPosition;
  savedToastPosition: ToastPosition;
  setToastPosition: (next: ToastPosition) => void;
  basemapTheme: MapBasemapTheme;
  savedBasemapTheme: MapBasemapTheme;
  basemapThemeOverrideEnabled: boolean;
  savedBasemapThemeOverrideEnabled: boolean;
  basemapThemeOverrideSelection: MapBasemapTheme;
  savedBasemapThemeOverrideSelection: MapBasemapTheme;
  setBasemapThemeOverrideEnabled: (next: boolean) => void;
  setBasemapTheme: (next: MapBasemapTheme) => void;
  savePreferences: (updates?: SavePreferencesPayload, options?: SavePreferencesOptions) => Promise<void>;
  resetPreferences: () => Promise<void>;
  refresh: () => Promise<void>;
  loading: boolean;
  error: string | null;
  navigation: NavigationPreferences;
  updateNavigation: (updates: Partial<NavigationPreferences>) => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

function isLayoutOption(value: unknown): value is LayoutOption {
  return value === 'side-menu' || value === 'top-menu';
}

function isToastPosition(value: unknown): value is ToastPosition {
  return value === 'top-left' || value === 'top-right' || value === 'bottom-left' || value === 'bottom-right';
}

function isMapBasemapTheme(value: unknown): value is MapBasemapTheme {
  return (
    value === 'light' ||
    value === 'dark' ||
    value === 'midnight' ||
    value === 'purple-dark' ||
    value === 'bubblegum' ||
    value === 'ocean' ||
    value === 'forest' ||
    value === 'aurora' ||
    value === 'ember' ||
    value === 'citrus' ||
    value === 'sandstone' ||
    value === 'slate' ||
    value === 'glacier' ||
    value === 'cyberpunk' ||
    value === 'rainbow' ||
    value === 'retro' ||
    value === 'noir'
  );
}

function prefersDarkScheme(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function parseBooleanPreference(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }
  return fallback;
}

function resolveBasemapTheme(theme: ThemeOption, systemPrefersDark: boolean): MapBasemapTheme {
  if (theme === 'auto') {
    return systemPrefersDark ? 'dark' : 'light';
  }
  return theme;
}

function readStoredLayout(): LayoutOption {
  if (typeof window === 'undefined') {
    return DEFAULT_LAYOUT;
  }
  try {
    const stored = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (isLayoutOption(stored)) {
      return stored;
    }
  } catch {
    // ignore storage failures
  }
  return DEFAULT_LAYOUT;
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const results = value
    .filter(item => typeof item === 'string' && item.trim().length > 0)
    .map(item => (item as string).trim());
  return Array.from(new Set(results));
}

function parseNavigationPreferences(value: unknown): NavigationPreferences {
  if (!value || typeof value !== 'object') {
    return DEFAULT_NAVIGATION_PREFERENCES;
  }
  const typed = value as Record<string, unknown>;
  const favorites = parseStringArray(typed.favorites);
  const recents = parseStringArray(typed.recents);
  const collapsed = typeof typed.collapsed === 'boolean' ? typed.collapsed : DEFAULT_NAVIGATION_PREFERENCES.collapsed;
  const favoritesOrder =
    typed.favoritesOrder === 'manual' || typed.favoritesOrder === 'alpha'
      ? (typed.favoritesOrder as 'alpha' | 'manual')
      : DEFAULT_NAVIGATION_PREFERENCES.favoritesOrder;
  return {
    favorites,
    recents,
    collapsed,
    favoritesOrder
  };
}

function sanitizeNavigationPreferences(preferences: NavigationPreferences): NavigationPreferences {
  return {
    favorites: Array.from(new Set(preferences.favorites.filter(Boolean))),
    recents: Array.from(new Set(preferences.recents.filter(Boolean))).slice(0, 8),
    collapsed: preferences.collapsed,
    favoritesOrder: preferences.favoritesOrder === 'manual' ? 'manual' : 'alpha'
  };
}

function navigationEquals(a: NavigationPreferences, b: NavigationPreferences): boolean {
  if (a.collapsed !== b.collapsed || a.favoritesOrder !== b.favoritesOrder) {
    return false;
  }
  if (a.favorites.length !== b.favorites.length || a.recents.length !== b.recents.length) {
    return false;
  }
  for (let i = 0; i < a.favorites.length; i += 1) {
    if (a.favorites[i] !== b.favorites[i]) {
      return false;
    }
  }
  for (let i = 0; i < a.recents.length; i += 1) {
    if (a.recents[i] !== b.recents[i]) {
      return false;
    }
  }
  return true;
}

function buildNavigationState(favorites: string[], recents: string[], collapsed: boolean): NavigationPreferences {
  return sanitizeNavigationPreferences({
    favorites,
    recents,
    collapsed,
    favoritesOrder: DEFAULT_NAVIGATION_PREFERENCES.favoritesOrder
  });
}

function parseCollapsed(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value === 'true';
  }
  return DEFAULT_NAVIGATION_PREFERENCES.collapsed;
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((val, index) => val === b[index]);
}

async function fetchPreferences(url: string): Promise<EffectivePreferencesResponse> {
  const response = await fetch(url, {
    credentials: 'include'
  });

  if (response.status === 304) {
    return {};
  }

  if (!response.ok) {
    const message = await response.text().catch(() => 'Failed to load preferences');
    throw new Error(message || 'Failed to load preferences');
  }

  return response.json() as Promise<EffectivePreferencesResponse>;
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const themeControllerRef = useRef<ThemeController | null>(null);
  const mountedRef = useRef(true);

  const getController = useCallback((): ThemeController | null => {
    const hasWindow = typeof window !== 'undefined';
    logDashboardTheme('getController:request', { hasWindow, hasInstance: !!themeControllerRef.current });
    if (!hasWindow) {
      logDashboardTheme('getController:no-window');
      return null;
    }
    if (!themeControllerRef.current) {
      logDashboardTheme('getController:create');
      themeControllerRef.current = new ThemeController();
    } else {
      logDashboardTheme('getController:reuse');
    }
    return themeControllerRef.current;
  }, []);

  const [theme, setTheme] = useState<ThemeOption>(DEFAULT_THEME);
  const [savedTheme, setSavedTheme] = useState<ThemeOption>(DEFAULT_THEME);
  const [themeEffect, setThemeEffectState] = useState<ThemeEffect>(DEFAULT_THEME_EFFECT);
  const [savedThemeEffect, setSavedThemeEffect] = useState<ThemeEffect>(DEFAULT_THEME_EFFECT);
  const [isThemePreview, setIsThemePreview] = useState(false);

  const [layout, setLayoutState] = useState<LayoutOption>(DEFAULT_LAYOUT);
  const [savedLayout, setSavedLayout] = useState<LayoutOption>(DEFAULT_LAYOUT);
  const [toastPosition, setToastPositionState] = useState<ToastPosition>(DEFAULT_TOAST_POSITION);
  const [savedToastPosition, setSavedToastPosition] = useState<ToastPosition>(DEFAULT_TOAST_POSITION);
  const [basemapThemeOverrideSelection, setBasemapThemeOverrideSelectionState] =
    useState<MapBasemapTheme>(DEFAULT_BASEMAP_THEME);
  const [savedBasemapThemeOverrideSelection, setSavedBasemapThemeOverrideSelection] =
    useState<MapBasemapTheme>(DEFAULT_BASEMAP_THEME);
  const [basemapThemeOverrideEnabled, setBasemapThemeOverrideEnabledState] = useState(false);
  const [savedBasemapThemeOverrideEnabled, setSavedBasemapThemeOverrideEnabled] = useState(false);
  const [systemPrefersDark, setSystemPrefersDark] = useState(prefersDarkScheme);

  const [navigation, setNavigation] = useState<NavigationPreferences>(DEFAULT_NAVIGATION_PREFERENCES);
  const [savedNavigation, setSavedNavigation] = useState<NavigationPreferences>(DEFAULT_NAVIGATION_PREFERENCES);
  const navigationRef = useRef(navigation);

  useEffect(() => {
    navigationRef.current = navigation;
  }, [navigation]);

  const [actionError, setActionError] = useState<string | null>(null);

  const { data, error: swrError, isLoading: isFetching, mutate } = useSWR<EffectivePreferencesResponse>(
    PREFERENCES_URL,
    fetchPreferences,
    {
      revalidateOnFocus: false,
      revalidateIfStale: true,
      revalidateOnReconnect: true
    }
  );

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const previewTheme = useCallback(
    (next: ThemeOption) => {
      logDashboardTheme('previewTheme', { next, savedTheme });
      const controller = getController();
      if (!controller) {
        setTheme(next);
        logDashboardTheme('previewTheme:no-controller', { applied: next });
        return;
      }

      if (next === savedTheme) {
        logDashboardTheme('previewTheme:matches-saved', { next });
        controller.exitPreviewMode();
        controller.setTheme(next, false);
        setTheme(next);
        setIsThemePreview(false);
        return;
      }

      logDashboardTheme('previewTheme:activate', { next });
      controller.previewTheme(next);
      setTheme(next);
      setIsThemePreview(true);
    },
    [getController, savedTheme]
  );

  const cancelThemePreview = useCallback(() => {
    const controller = getController();
    const target = savedTheme;
    logDashboardTheme('cancelThemePreview', { target, hasController: !!controller });
    if (controller) {
      controller.exitPreviewMode();
      controller.setTheme(target, false);
    }
    setTheme(target);
    setIsThemePreview(false);
  }, [getController, savedTheme]);

  const setThemeEffect = useCallback(
    (next: ThemeEffect) => {
      logDashboardTheme('setThemeEffect', { next });
      setThemeEffectState(next);
      const controller = getController();
      controller?.setEffect(next, false);
    },
    [getController]
  );

  const setLayout = useCallback((next: LayoutOption) => {
    logDashboardTheme('setLayout', { next });
    setLayoutState(next);
  }, []);

  const setToastPosition = useCallback((next: ToastPosition) => {
    logDashboardTheme('setToastPosition', { next });
    setToastPositionState(next);
  }, []);

  const setBasemapTheme = useCallback((next: MapBasemapTheme) => {
    logDashboardTheme('setBasemapTheme', { next });
    setBasemapThemeOverrideSelectionState(next);
  }, []);

  const setBasemapThemeOverrideEnabled = useCallback((next: boolean) => {
    logDashboardTheme('setBasemapThemeOverrideEnabled', { next });
    setBasemapThemeOverrideEnabledState(next);
  }, []);

  const applyLayoutLocally = useCallback((next: LayoutOption) => {
    logDashboardTheme('applyLayoutLocally', { next });
    setLayoutState(next);
    setSavedLayout(next);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(LAYOUT_STORAGE_KEY, next);
      } catch {
        // ignore storage failures
        logDashboardTheme('applyLayoutLocally:storage-error', { next });
      }
    }
  }, []);

  useEffect(() => {
    const controller = getController();
    if (controller) {
      const initialTheme = (controller.getTheme() as ThemeOption) ?? DEFAULT_THEME;
      const storedTheme = (controller.getSavedTheme() as ThemeOption) ?? initialTheme;
      const initialEffect = (controller.getEffect() as ThemeEffect) ?? DEFAULT_THEME_EFFECT;
      const storedEffect = (controller.getSavedEffect() as ThemeEffect) ?? initialEffect;
      setTheme(initialTheme);
      setSavedTheme(storedTheme);
      setThemeEffectState(initialEffect);
      setSavedThemeEffect(storedEffect);
      setIsThemePreview(controller.isInPreviewMode());
      logDashboardTheme('controller:init-state', {
        initialTheme,
        storedTheme,
        initialEffect,
        storedEffect,
        previewMode: controller.isInPreviewMode()
      });
    } else {
      logDashboardTheme('controller:init-missing');
    }
    const storedLayout = readStoredLayout();
    logDashboardTheme('layout:init-state', { storedLayout });
    setLayoutState(storedLayout);
    setSavedLayout(storedLayout);
  }, [getController]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };
    setSystemPrefersDark(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!data) {
      return;
    }

    const controller = getController();
    const themePref = (data.keys?.['ui.theme']?.value as ThemeOption | undefined) ?? DEFAULT_THEME;
    const rawEffect = data.keys?.[THEME_EFFECT_KEY]?.value;
    const effectPref = isThemeEffect(rawEffect) ? rawEffect : DEFAULT_THEME_EFFECT;
    const rawLayout = data.keys?.['ui.layout']?.value;
    const layoutPref = isLayoutOption(rawLayout) ? rawLayout : DEFAULT_LAYOUT;
    const rawToastPosition = data.keys?.[TOAST_POSITION_KEY]?.value;
    const toastPositionPref = isToastPosition(rawToastPosition) ? rawToastPosition : DEFAULT_TOAST_POSITION;
    const rawBasemapTheme = data.keys?.[BASEMAP_THEME_KEY]?.value;
    const basemapThemePref = isMapBasemapTheme(rawBasemapTheme) ? rawBasemapTheme : DEFAULT_BASEMAP_THEME;
    const basemapThemeOverrideEnabledPref = parseBooleanPreference(data.keys?.[BASEMAP_THEME_OVERRIDE_KEY]?.value, false);
    logDashboardTheme('swr:data', {
      themePref,
      effectPref,
      layoutPref,
      toastPositionPref,
      basemapThemePref,
      basemapThemeOverrideEnabledPref,
      hasController: !!controller,
      keys: data.keys ? Object.keys(data.keys) : []
    });

    controller?.exitPreviewMode();
    controller?.saveTheme(themePref);
    controller?.setEffect(effectPref, false);

    setTheme(themePref);
    setSavedTheme(themePref);
    setIsThemePreview(false);
    setThemeEffectState(effectPref);
    setSavedThemeEffect(effectPref);
    setToastPositionState(toastPositionPref);
    setSavedToastPosition(toastPositionPref);
    setBasemapThemeOverrideSelectionState(basemapThemePref);
    setSavedBasemapThemeOverrideSelection(basemapThemePref);
    setBasemapThemeOverrideEnabledState(basemapThemeOverrideEnabledPref);
    setSavedBasemapThemeOverrideEnabled(basemapThemeOverrideEnabledPref);

    applyLayoutLocally(layoutPref);

    const favoritePref = parseStringArray(data.keys?.[NAV_FAVORITES_KEY]?.value);
    const recentsPref = parseStringArray(data.keys?.[NAV_RECENTS_KEY]?.value);
    const collapsedPref = parseCollapsed(data.keys?.[NAV_COLLAPSED_KEY]?.value);
    const hasFavoritesPref = Object.prototype.hasOwnProperty.call(data.keys ?? {}, NAV_FAVORITES_KEY);
    const favoritesOrderRaw = data.keys?.[NAV_FAVORITES_ORDER_KEY]?.value ?? data.keys?.[NAV_FAVORITES_ORDER_KEY_LEGACY]?.value;
    const favoritesOrderPref = favoritesOrderRaw === 'manual' ? 'manual' : DEFAULT_NAVIGATION_PREFERENCES.favoritesOrder;
    const navigationPref = sanitizeNavigationPreferences({
      favorites: hasFavoritesPref ? favoritePref : [...DEFAULT_NAVIGATION_PREFERENCES.favorites],
      recents: recentsPref,
      collapsed: collapsedPref,
      favoritesOrder: favoritesOrderPref
    });
    logDashboardTheme('swr:navigation', navigationPref);
    setNavigation(navigationPref);
    setSavedNavigation(navigationPref);
  }, [applyLayoutLocally, data, getController]);

  useEffect(() => {
    if (!swrError) {
      if (!isFetching) {
        setActionError(null);
      }
      return;
    }
    const message = swrError instanceof Error ? swrError.message : 'Failed to load preferences';
    setActionError(message);
  }, [swrError, isFetching]);

  const basemapTheme = useMemo(
    () =>
      basemapThemeOverrideEnabled
        ? basemapThemeOverrideSelection
        : resolveBasemapTheme(theme, systemPrefersDark),
    [basemapThemeOverrideEnabled, basemapThemeOverrideSelection, systemPrefersDark, theme]
  );

  const savedBasemapTheme = useMemo(
    () =>
      savedBasemapThemeOverrideEnabled
        ? savedBasemapThemeOverrideSelection
        : resolveBasemapTheme(savedTheme, systemPrefersDark),
    [savedBasemapThemeOverrideEnabled, savedBasemapThemeOverrideSelection, savedTheme, systemPrefersDark]
  );

  const refresh = useCallback(async () => {
    logDashboardTheme('refresh');
    await mutate();
  }, [mutate]);

  const savePreferences = useCallback(
    async (updates: SavePreferencesPayload = {}, options?: SavePreferencesOptions) => {
      logDashboardTheme('savePreferences:start', { updates });
      const controller = getController();
      const payload: Array<{ key: string; value: unknown; scope: 'USER' }> = [];
      const {
        theme: nextTheme,
        themeEffect: nextEffect,
        layout: nextLayout,
        toastPosition: nextToastPosition,
        basemapTheme: nextBasemapTheme,
        basemapThemeOverrideEnabled: nextBasemapThemeOverrideEnabled
      } = updates;
      let pendingNavigation = options?.resolvedNavigation ?? navigation;

      if (nextTheme !== undefined && nextTheme !== savedTheme) {
        payload.push({ key: 'ui.theme', value: nextTheme, scope: 'USER' });
      }
      if (nextEffect !== undefined && nextEffect !== savedThemeEffect) {
        payload.push({ key: THEME_EFFECT_KEY, value: nextEffect, scope: 'USER' });
      }
      if (nextLayout !== undefined && nextLayout !== savedLayout) {
        payload.push({ key: 'ui.layout', value: nextLayout, scope: 'USER' });
      }
      if (nextToastPosition !== undefined && nextToastPosition !== savedToastPosition) {
        payload.push({ key: TOAST_POSITION_KEY, value: nextToastPosition, scope: 'USER' });
      }
      if (nextBasemapTheme !== undefined && nextBasemapTheme !== savedBasemapThemeOverrideSelection) {
        payload.push({ key: BASEMAP_THEME_KEY, value: nextBasemapTheme, scope: 'USER' });
      }
      if (
        nextBasemapThemeOverrideEnabled !== undefined &&
        nextBasemapThemeOverrideEnabled !== savedBasemapThemeOverrideEnabled
      ) {
        payload.push({
          key: BASEMAP_THEME_OVERRIDE_KEY,
          value: nextBasemapThemeOverrideEnabled,
          scope: 'USER'
        });
      }
      if (updates.navigation || options?.resolvedNavigation) {
        const merged =
          options?.resolvedNavigation ??
          sanitizeNavigationPreferences({
            favorites: updates.navigation?.favorites ?? navigation.favorites,
            recents: updates.navigation?.recents ?? navigation.recents,
            collapsed: updates.navigation?.collapsed ?? navigation.collapsed,
            favoritesOrder: updates.navigation?.favoritesOrder ?? navigation.favoritesOrder
          });
        pendingNavigation = merged;
        if (!arraysEqual(merged.favorites, savedNavigation.favorites)) {
          payload.push({ key: NAV_FAVORITES_KEY, value: merged.favorites, scope: 'USER' });
        }
        if (!arraysEqual(merged.recents, savedNavigation.recents)) {
          payload.push({ key: NAV_RECENTS_KEY, value: merged.recents, scope: 'USER' });
        }
        if (merged.collapsed !== savedNavigation.collapsed) {
          payload.push({ key: NAV_COLLAPSED_KEY, value: merged.collapsed, scope: 'USER' });
        }
        if (merged.favoritesOrder !== savedNavigation.favoritesOrder) {
          payload.push({ key: NAV_FAVORITES_ORDER_KEY, value: merged.favoritesOrder, scope: 'USER' });
        }
      }

      if (!payload.length) {
        logDashboardTheme('savePreferences:no-changes', { nextTheme, nextLayout });
        controller?.exitPreviewMode();
        controller?.setTheme(savedTheme, false);
        setTheme(savedTheme);
        setIsThemePreview(false);
        setLayoutState(savedLayout);
        setToastPositionState(savedToastPosition);
        setBasemapThemeOverrideSelectionState(savedBasemapThemeOverrideSelection);
        setBasemapThemeOverrideEnabledState(savedBasemapThemeOverrideEnabled);
        setNavigation(savedNavigation);
        return;
      }

      try {
        logDashboardTheme('savePreferences:request', { payload });
        const response = await fetch('/api/v1/preferences:bulkSet', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items: payload })
        });

        if (!response.ok) {
          const message = await response.text().catch(() => 'Failed to update preferences');
          throw new Error(message || 'Failed to update preferences');
        }

        controller?.exitPreviewMode();
        if (nextTheme !== undefined && nextTheme !== savedTheme) {
          controller?.saveTheme(nextTheme);
          setTheme(nextTheme);
          setSavedTheme(nextTheme);
          setIsThemePreview(false);
          logDashboardTheme('savePreferences:applied-theme', { nextTheme });
        }
        if (nextEffect !== undefined && nextEffect !== savedThemeEffect) {
          controller?.saveEffect(nextEffect);
          setThemeEffectState(nextEffect);
          setSavedThemeEffect(nextEffect);
          logDashboardTheme('savePreferences:applied-effect', { nextEffect });
        }

        if (nextLayout !== undefined && nextLayout !== savedLayout) {
          applyLayoutLocally(nextLayout);
          logDashboardTheme('savePreferences:applied-layout', { nextLayout });
        }
        if (nextToastPosition !== undefined && nextToastPosition !== savedToastPosition) {
          setToastPositionState(nextToastPosition);
          setSavedToastPosition(nextToastPosition);
          logDashboardTheme('savePreferences:applied-toast-position', { nextToastPosition });
        }
        if (nextBasemapTheme !== undefined && nextBasemapTheme !== savedBasemapThemeOverrideSelection) {
          setBasemapThemeOverrideSelectionState(nextBasemapTheme);
          setSavedBasemapThemeOverrideSelection(nextBasemapTheme);
          logDashboardTheme('savePreferences:applied-basemap-theme', { nextBasemapTheme });
        }
        if (
          nextBasemapThemeOverrideEnabled !== undefined &&
          nextBasemapThemeOverrideEnabled !== savedBasemapThemeOverrideEnabled
        ) {
          setBasemapThemeOverrideEnabledState(nextBasemapThemeOverrideEnabled);
          setSavedBasemapThemeOverrideEnabled(nextBasemapThemeOverrideEnabled);
          logDashboardTheme('savePreferences:applied-basemap-theme-override', {
            nextBasemapThemeOverrideEnabled
          });
        }
        if ((updates.navigation || options?.resolvedNavigation) && !navigationEquals(pendingNavigation, savedNavigation)) {
          setNavigation(pendingNavigation);
          setSavedNavigation(pendingNavigation);
          logDashboardTheme('savePreferences:applied-navigation');
        }

        setActionError(null);
        if (!options?.skipRevalidate) {
          await mutate();
        }
        logDashboardTheme('savePreferences:complete');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update preferences';
        setActionError(message);
        logDashboardTheme('savePreferences:error', { message });
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [
      applyLayoutLocally,
      getController,
      mutate,
      navigation,
      savedBasemapThemeOverrideEnabled,
      savedBasemapThemeOverrideSelection,
      savedLayout,
      savedNavigation,
      savedTheme,
      savedThemeEffect,
      savedToastPosition
    ]
  );

  const resetPreferences = useCallback(async () => {
    logDashboardTheme('resetPreferences:start');
    try {
      const response = await fetch('/api/v1/preferences:reset', {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const message = await response.text().catch(() => 'Failed to reset preferences');
        throw new Error(message || 'Failed to reset preferences');
      }

      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem(LAYOUT_STORAGE_KEY);
        } catch {
          // ignore storage failures
          logDashboardTheme('resetPreferences:storage-error');
        }
      }

      setNavigation(DEFAULT_NAVIGATION_PREFERENCES);
      setSavedNavigation(DEFAULT_NAVIGATION_PREFERENCES);

      setActionError(null);
      await mutate();
      logDashboardTheme('resetPreferences:complete');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset preferences';
      setActionError(message);
      logDashboardTheme('resetPreferences:error', { message });
      throw err instanceof Error ? err : new Error(message);
    }
  }, [mutate]);

  const updateNavigation = useCallback(
    async (updates: Partial<NavigationPreferences>) => {
      logDashboardTheme('navigation:update:start', updates);
      const base = navigationRef.current;
      const optimistic = sanitizeNavigationPreferences({
        favorites: updates.favorites ?? base.favorites,
        recents: updates.recents ?? base.recents,
        collapsed: updates.collapsed ?? base.collapsed,
        favoritesOrder: updates.favoritesOrder ?? base.favoritesOrder
      });
      logDashboardTheme('navigation:update:optimistic', optimistic);
      const previous = base;
      setNavigation(optimistic);
      try {
        await savePreferences({ navigation: updates }, { skipRevalidate: true, resolvedNavigation: optimistic });
        logDashboardTheme('navigation:update:success');
      } catch (error) {
        logDashboardTheme('navigation:update:error', { message: error instanceof Error ? error.message : String(error) });
        setNavigation(previous);
        throw error;
      }
    },
    [navigation, savePreferences]
  );

  const value = useMemo<PreferencesContextValue>(
    () => ({
      theme,
      savedTheme,
      themeEffect,
      savedThemeEffect,
      isThemePreview,
      previewTheme,
      cancelThemePreview,
      setThemeEffect,
      layout,
      savedLayout,
      setLayout,
      toastPosition,
      savedToastPosition,
      setToastPosition,
      basemapTheme,
      savedBasemapTheme,
      basemapThemeOverrideEnabled,
      savedBasemapThemeOverrideEnabled,
      basemapThemeOverrideSelection,
      savedBasemapThemeOverrideSelection,
      setBasemapThemeOverrideEnabled,
      setBasemapTheme,
      savePreferences,
      resetPreferences,
      refresh,
      loading: isFetching && !data,
      error: actionError,
      navigation,
      updateNavigation
    }),
    [
      actionError,
      basemapTheme,
      basemapThemeOverrideEnabled,
      basemapThemeOverrideSelection,
      cancelThemePreview,
      data,
      isFetching,
      isThemePreview,
      layout,
      navigation,
      previewTheme,
      refresh,
      savePreferences,
      resetPreferences,
      savedBasemapTheme,
      savedBasemapThemeOverrideEnabled,
      savedBasemapThemeOverrideSelection,
      savedLayout,
      savedTheme,
      savedThemeEffect,
      savedToastPosition,
      setBasemapTheme,
      setBasemapThemeOverrideEnabled,
      setLayout,
      setToastPosition,
      setThemeEffect,
      theme,
      themeEffect,
      toastPosition,
      updateNavigation
    ]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}
